import { Grid, Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { InfoCard } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { IInfoCardLine } from 'core/types';
import { formatDate, getValue } from 'core/utils';
import { IJob } from 'jobs/types';
import { Direction, Level } from 'theme/enums';

import { UpdateBaseInformationButton } from './UpdateBaseInformation';

interface IProps {
  job: IJob;
}

export const BaseInformation = ({ job }: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();

  const isEditable =
    job.access?.editAllowed && hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_DETAILS_EDIT);

  const mainInfoItems: IInfoCardLine[] = [
    {
      label: t('labelJobNumber'),
      value: getValue(job, 'id'),
    },
    {
      label: t('labelJobStatus'),
      value: getValue(job, 'status'),
    },
    {
      label: t('labelCountry'),
      value: getValue(job, 'externalLocationCountry'),
    },
    {
      label: t('labelTheatre'),
      value: getValue(job, 'externalLocationRegion'),
    },
    {
      label: t('labelDiscipline'),
      value: getValue(job, 'disciplineName'),
    },
    {
      label: t('labelCustomerCompany'),
      value: getValue(job, 'customerCompanyName'),
    },
    {
      label: t('labelCustomerName'),
      value: [getValue(job, 'customerFirstName', ''), getValue(job, 'customerLastName', '')]
        .filter(Boolean)
        .join(' '),
    },
    {
      label: t('labelCustomerAccountNumber'),
      value: getValue(job, 'customerNumber'),
    },
    {
      label: t('labelCreatedDate'),
      value: formatDate(job.createdAt),
    },
    {
      label: t('labelDueDate'),
      value: formatDate(job.dueDate),
    },
    {
      label: t('labelSupportCompletedDate'),
      value: formatDate(job.completedDate),
    },
  ];

  const createdByUserItems: IInfoCardLine[] = [
    {
      label: t('labelCreatedByUser'),
      value: getValue(job, 'ownerUserName'),
    },
    {
      label: t('labelCreatedByUserEmployeeId'),
      value: getValue(job, 'ownerUserEmployeeId'),
    },
    {
      label: t('labelCreatedByUserLocation'),
      value: getValue(job, 'ownerLocationName'),
    },
  ];

  const requestedByUserItems: IInfoCardLine[] = [
    {
      label: t('labelRequestedByUser'),
      value: getValue(job, 'requesterUserName'),
    },
    {
      label: t('labelRequestedByUserEmployeeId'),
      value: getValue(job, 'requesterUserEmployeeId'),
    },
    {
      label: t('labelRequestedByUserLocation'),
      value: getValue(job, 'requesterUserExternalLocationRegion'),
    },
    {
      label: t('labelRequestedByUserBusinessUnit'),
      value: getValue(job, 'requesterUserBusinessUnit'),
    },
  ];

  const supportUserItems: IInfoCardLine[] = [
    {
      label: t('labelSupportUserAssigned'),
      value: getValue(job, 'supportUserName'),
    },
    {
      label: t('labelSupportUserAssignedEmployeeId'),
      value: getValue(job, 'supportUserEmployeeId'),
    },
    {
      label: t('labelSupportUserAssignedLocation'),
      value: getValue(job, 'supportUserLocationName'),
    },
  ];

  return (
    <Stack>
      <Stack direction={Direction.ROW} justifyContent="space-between" alignItems="center">
        <Typography level={Level.H4}>{t('titleBaseInformation')}</Typography>
        {isEditable && <UpdateBaseInformationButton job={job} />}
      </Stack>

      <Grid container spacing={1}>
        <Grid xs={12} md={6}>
          <InfoCard items={mainInfoItems} />
        </Grid>

        <Grid xs={12} md={6}>
          <Stack>
            <InfoCard
              items={[...createdByUserItems, ...requestedByUserItems, ...supportUserItems]}
            />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
